import React, { PureComponent } from 'react';
import './styles.css';

import get from 'lodash/get';
import PropTypes from 'prop-types';
import Dropdown from '../Dropdown';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Route } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { getConfig } from '../../config/portal';
import { OptionSet, Option } from '../OptionSet';
import messages from '../../utils/translationMessages';
import { addEvent } from '../../store/actions/dataLayer';
import { getBoatsConstants } from '../../constants/boats';
import { PRODUCT_CATEGORY, TRACKING_EVENTS } from '../../constants/dataLayer';
import { generateSearchPath as generateSearchBoatsPath } from '../../utils/urlHelpers/boats';
import { generateSearchPath as generateSearchEnginesPath } from '../../utils/urlHelpers/engines';

class KeywordSearch extends PureComponent {
  inputRef = React.createRef();

  state = {
    inputValue: '',
    searchType: []
  };

  supportsFeature = feature => {
    return getConfig().supports[feature];
  }

  validateKeyword = word => {
    const keywordPattern = getBoatsConstants().KEYWORD_SEARCH_PATTERN;
    const validationRegExp = RegExp(keywordPattern);
    return !word.length || validationRegExp.test(word);
  }

  handleInputFocus = () => {
    this.inputRef.current.focus();
  }

  handleInputChange = e => {
    const searchKeyword = e.target.value;
    this.validateKeyword(searchKeyword) && this.setState({ inputValue: searchKeyword });
  }

  getParams = () => ({
    keyword: this.state.inputValue,
    searchType: this.state.searchType
  });

  handleSubmit = (history) => (e) => {
    e.preventDefault();

    if (this.state.inputValue.trim().length) {
      const params = this.getParams();
      const engines = getBoatsConstants().SEARCH_TYPES.engines;
      const searchType = params.searchType[0] && params.searchType[0].value;
      const supportsEngines = this.supportsFeature(engines);

      this.props.addEvent(TRACKING_EVENTS.KEYWORD_SEARCH, {
        keyword: `${params.keyword}`.trim().toLowerCase(),
        'product_category': searchType === engines && supportsEngines ? PRODUCT_CATEGORY.ENGINE : PRODUCT_CATEGORY.BOAT
      });

      const keywordSearchOptions = get(this.props, 'keywordSearchOptions.paramFormat', '');
      const requiresReload = get(this.props, 'keywordSearchOptions.requiresReload', false);
      params.searchKeywordParamFormat = keywordSearchOptions;

      let path = searchType === engines && supportsEngines ?
        generateSearchEnginesPath(params) :
        generateSearchBoatsPath(params);

      if (keywordSearchOptions === 'query-string'){
        path = path.replace('%3F','?');
      }

      if (requiresReload) {
        window.location.assign(path);
      } else {
        history.push(path);
        this.setState({ inputValue: '' });
      }
    }
  }

  handleSelectChange = value => {
    this.setState({ searchType: value });
    this.handleInputFocus();
  }

  render() {
    const { intl: { formatMessage: t } } = this.props;
    const searchTypes = [{ value: getBoatsConstants().SEARCH_TYPES.boats, label: t(messages.searchTypeBoats) }];
    if (this.supportsFeature(getBoatsConstants().SEARCH_TYPES.engines)) {
      searchTypes.push({ value: getBoatsConstants().SEARCH_TYPES.engines, label: t(messages.searchTypeEngines) });
    }

    return (
      <Route render={({ history }) => (
        <div className="keyword-search">
          <svg onClick={this.handleInputFocus} width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.50126 8.38675C9.36666 8.25189 9.18395 8.1761 8.99341 8.1761H8.79039C8.73486 8.1761 8.68149 8.15456 8.64152 8.11601C8.55968 8.0371 8.55406 7.90833 8.62452 7.81911C9.27551 6.9948 9.66266 5.95572 9.66266 4.83133C9.66266 2.16295 7.49971 0 4.83133 0C2.16295 0 0 2.16295 0 4.83133C0 7.49971 2.16295 9.66266 4.83133 9.66266C5.95572 9.66266 6.9948 9.27551 7.81911 8.62452C7.90833 8.55406 8.0371 8.55968 8.11601 8.64152C8.15456 8.68149 8.1761 8.73486 8.1761 8.79039V8.99341C8.1761 9.18395 8.25189 9.36666 8.38675 9.50126L11.3382 12.4468C11.6444 12.7524 12.1403 12.7522 12.4463 12.4463C12.7522 12.1403 12.7524 11.6444 12.4468 11.3382L9.50126 8.38675ZM4.83133 8.1761C2.98056 8.1761 1.48656 6.6821 1.48656 4.83133C1.48656 2.98056 2.98056 1.48656 4.83133 1.48656C6.6821 1.48656 8.1761 2.98056 8.1761 4.83133C8.1761 6.6821 6.6821 8.1761 4.83133 8.1761Z" fill="#2C272D"/>
          </svg>
          <form onSubmit={this.handleSubmit(history)}>
            <input
              className="keyword-input"
              ref={this.inputRef}
              placeholder={t(messages.header.keywordSearch)}
              value={this.state.inputValue}
              onChange={this.handleInputChange}
              aria-label="Keyword Search"
            />
          </form>
          {this.supportsFeature(getBoatsConstants().SEARCH_TYPES.engines) ?
            <Dropdown
              label={t(messages.header.keywordSearch)}
              setSize={true}
              multiple={false}
              setValue={this.handleSelectChange}
              options={searchTypes}
              optionKey="label"
              value={this.state.searchType.length ? this.state.searchType : [searchTypes[0]]}
              render={(options, inheritProps) => (
                <OptionSet {...inheritProps}>
                  {options.map((option, idx) => {
                    return (
                      <Option key={'searchType-' + idx} option={option} {...inheritProps}>
                        {option.label}
                      </Option>
                    );
                  })}
                </OptionSet>
              )}
            /> : null}
        </div>
      )} />
    );
  }
}

KeywordSearch.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  addEvent: PropTypes.func.isRequired
};

export default connect(null, (dispatch) =>
  bindActionCreators({addEvent}, dispatch)
)(injectIntl(KeywordSearch));
